import React, { createContext, useState, useEffect } from "react";
import {
  IContextProver,
  IPropsContext,
  PAGE_INICIAL,
  MaintenanceData,
} from "./ContextProverModel";
import { useOidc, useOidcAccessToken } from "@axa-fr/react-oidc";
import { getMaintenance } from "#/api/getMaintenance";
import { Conta } from "#/api/getAgenciaConta";
import { findProductsByCommissioned, IProduct } from "#/api/getProducts";
import {
  getValorDecodeBase64,
  loadSessionStorage,
  saveSessionStorage,
  verificarUserQuantidadesConta,
} from "./ContextProverServices";
import { IBensData } from "#/api/getBens";
import { IGetVendaResumoData } from "#/api/getVendaResumo";
import { IGetVendasPlanosCobrancaData } from "#/api/getVendasPlanosCobranca";
import {
  getVendasListaProposta,
  IGetVendasListaPropostaData,
} from "#/api/getVendasListaProposta";
import { IGetVendasBoletoData } from "#/api/getVendasBoleto";
import { IGetVendasContratoData } from "#/api/getVendasContrato";
import { verificarElegibilidade } from "#/containers/AccountAgency/service";
import { FEED } from "#/api/associadosFeedBackLogin";
import { toast } from "@sicredi/react";

export const ContextAPI = createContext<IContextProver>({} as IContextProver);
export const ContextProver: React.FC<IPropsContext> = ({
  children,
}: React.PropsWithChildren<IPropsContext>) => {
  const { accessTokenPayload } = useOidcAccessToken();
  const { logout } = useOidc();

  const [products, setProducts] = useState<IProduct[]>([]);
  const [agencyAccountCommissioned, setAgencyAccountCommissioned] =
    useState<Conta>({} as Conta);

  const [maintenanceData, setMaintenanceData] =
    useState<MaintenanceData | null>(null);

  const [homePageInitial, setHomePageInitial] = useState<
    PAGE_INICIAL | FEED | null
  >(null);
  const [idProduto, setIdProduto] = useState(0);
  const [idComercial, setIdComercial] = useState(0);
  const [valorParcela, setValorParcela] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [idSimulacao, setIdSimulacao] = useState(0);
  const [valorBem, setValorBem] = useState(0);
  const [idDocumento, setIdDocumento] = useState(0);
  const [isCheckAceitoContrato, setIsCheckAceitoContrato] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isHasAuthConta, setIsHasAuthConta] = useState<Conta | null>(null);
  const [isMenuConsorcio, setIsMenuConsorcio] = useState(false);

  const [vendasContrato, setVendasContrato] = useState<IGetVendasContratoData>(
    {} as IGetVendasContratoData
  );
  const [vendaBoleto, setVendaBoleto] = useState<IGetVendasBoletoData>(
    {} as IGetVendasBoletoData
  );

  const [vendasListaProposta, setVendasListaProposta] =
    useState<IGetVendasListaPropostaData>({} as IGetVendasListaPropostaData);

  const [vendasPlanosCobranca, setVendasPlanosCobranca] =
    useState<IGetVendasPlanosCobrancaData>({} as IGetVendasPlanosCobrancaData);

  const [bens, setBens] = useState<IBensData>({} as IBensData);

  const [vendasResumo, setVendasResumo] = useState<IGetVendaResumoData>(
    {} as IGetVendaResumoData
  );
  const [isOpenNotFoundModal, setIsOpenNotFoundModal] = useState(false);
  const [isOpenCapitalIntegralizadoModal, setIsOpenCapitalIntegralizadoModal] =
    useState(false);
  const [isOpenOfertaIndisponivelModal, setIsOpenOfertaIndisponivelModal] =
    useState(false);
  const [isOpenCadastroErroModal, setIsOpenCadastroErroModal] = useState(false);

  const userCPF = accessTokenPayload?.preferred_username as string;
  const codigoComissionado = getValorDecodeBase64(
    agencyAccountCommissioned?.codigoComissionado
  );
  const documento = getValorDecodeBase64(
    agencyAccountCommissioned?.documentoTitular
  );
  const selectedCatalogProduct = products?.find(
    (item) => item.idComercial === idComercial
  );
  const idSubProduto = products?.find(
    (item) => item.idProduto === idProduto
  )?.idSubProduto;
  const situacaoGrupo = "F";
  const idTipoVenda = 1;
  const seguro = "S";

  const startAppData = async () => {
    try {
      const maintenance = await getMaintenance();
      setMaintenanceData(maintenance);

      if (maintenance.emManutencao) {
        window.isMaintenance = true;
        return;
      }

      window.isMaintenance = false;
      const isHasAuthContaStorage = loadSessionStorage("isHasAuthConta");

      if (!isHasAuthContaStorage) {
        const agenciaContaUser = await verificarUserQuantidadesConta({
          documento: userCPF,
        });
        if (agenciaContaUser) {
          saveSessionStorage("isHasAuthConta", agenciaContaUser);
          setTimeout(() => {
            setIsHasAuthConta(loadSessionStorage("isHasAuthConta"));
            window.location.reload();
          }, 1000);
        } else {
          setHomePageInitial(PAGE_INICIAL.Home);
        }
      }

      if (isHasAuthContaStorage) {
        setIsHasAuthConta(isHasAuthContaStorage);
        setAgencyAccountCommissioned(isHasAuthContaStorage);
        const documento = getValorDecodeBase64(
          isHasAuthContaStorage.documentoTitular
        );
        const codigoComissionado = getValorDecodeBase64(
          isHasAuthContaStorage.codigoComissionado
        );
        const conta = getValorDecodeBase64(isHasAuthContaStorage.conta);
        const cooperativa = getValorDecodeBase64(isHasAuthContaStorage.agencia);

        const feedBack = await verificarElegibilidade({
          conta,
          cooperativa,
          codigoComissionado,
          documento,
        });
        if (feedBack === FEED.CAPITAL_INTEGRALIZADO) {
          setIsOpenCapitalIntegralizadoModal(true);
          setHomePageInitial(FEED.CAPITAL_INTEGRALIZADO);
          return;
        }

        const products = await findProductsByCommissioned(codigoComissionado);
        setProducts(products.data);

        const vendasListaProposta = await getVendasListaProposta({
          codigoComissionado,
          documento,
        });
        setVendasListaProposta(vendasListaProposta);

        setHomePageInitial(PAGE_INICIAL.Home);
      }
    } catch (e) {
      toast.error(e);
    }
  };

  useEffect(() => {
    startAppData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCPF]);

  useEffect(() => {
    if (selectedCatalogProduct) {
      setIdProduto(selectedCatalogProduct.idProduto);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idComercial, products]);

  useEffect(() => {
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  const checkMobile = () => {
    const userAgent = navigator.userAgent;

    if (
      /android/i.test(userAgent) ||
      /iPad|iPhone|iPod/.test(userAgent) ||
      (/Mac/i.test(userAgent) && "ontouchend" in document)
    ) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  };

  return (
    <ContextAPI.Provider
      value={{
        maintenanceData,
        homePageInitial,
        userCPF,
        documento,
        products,
        setProducts,
        agencyAccountCommissioned,
        setAgencyAccountCommissioned,
        codigoComissionado,
        idProduto,
        setIdProduto,
        idSubProduto,
        idComercial,
        setIdComercial,
        valorParcela,
        setValorParcela,
        isMobile,
        situacaoGrupo,
        idTipoVenda,
        idSimulacao,
        setIdSimulacao,
        bens,
        setBens,
        vendasResumo,
        setVendasResumo,
        valorBem,
        setValorBem,
        idDocumento,
        setIdDocumento,
        seguro,
        vendasPlanosCobranca,
        setVendasPlanosCobranca,
        vendasListaProposta,
        setVendasListaProposta,
        vendaBoleto,
        setVendaBoleto,
        vendasContrato,
        setVendasContrato,
        isCheckAceitoContrato,
        setIsCheckAceitoContrato,
        isOpenNotFoundModal,
        setIsOpenNotFoundModal,
        isOpenCapitalIntegralizadoModal,
        setIsOpenCapitalIntegralizadoModal,
        isOpenOfertaIndisponivelModal,
        setIsOpenOfertaIndisponivelModal,
        isOpenCadastroErroModal,
        setIsOpenCadastroErroModal,
        logout,
        isLoading,
        setIsLoading,
        isHasAuthConta,
        setIsHasAuthConta,
        isMenuConsorcio,
        setIsMenuConsorcio,
      }}
    >
      {children}
    </ContextAPI.Provider>
  );
};
